// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import {
  Badge,
  Box,
  Button,
  CloseIcon,
  Column,
  Combobox,
  Dialog,
  FilterIcon,
  Menu,
  MenuItem,
  MenuList,
  PlusIcon,
  Row,
  Select,
  Text,
  TextInput,
  useDisclosure,
  MenuIconButton,
  MenuDivider,
  BadgeGroup,
  BadgeButton,
  TextWithTooltip,
} from "@hightouch/htui";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import {
  AndOrToggleButton,
  DeleteButton,
} from "src/components/explore/visual/condition-buttons";
import { colors } from "src/components/explore/visual/colors";
import { GroupIndicatorBar } from "src/components/explore/visual/group-indicator-bar";
import {
  ColumnType,
  ConditionType,
  DefaultOperators,
  IntervalOperators,
  OperatorOptionsWithIsIn,
  OperatorsWithoutValue,
} from "src/types/visual";

import type {
  FeatureMatrixFormValues,
  FilterableColumn,
  InlineFilter,
} from "src/pages/decision-engines/configuration/feature-matrices/types";

const VALUELESS_OPERATORS = new Set(["exists", "notexists"]);

function unwrapConditionLeaves(conditions: unknown[]): unknown[] {
  const leaves: unknown[] = [];
  for (const c of conditions) {
    const cond = c as { type?: string; conditions?: unknown[] };
    if (
      (cond.type === "and" || cond.type === "or") &&
      Array.isArray(cond.conditions)
    ) {
      leaves.push(...cond.conditions);
    } else {
      leaves.push(c);
    }
  }
  return leaves;
}

type GroupConditionType = ConditionType.And | ConditionType.Or;

function getGroupConditionType(conditions: unknown[]): GroupConditionType {
  const [group] = conditions as ({ type?: string } | undefined)[];
  return group?.type === ConditionType.Or
    ? ConditionType.Or
    : ConditionType.And;
}

export function inferFilterName(conditions: unknown[]): string {
  if (!conditions.length) return "Empty filter";

  const parts: string[] = [];
  for (const c of unwrapConditionLeaves(conditions)) {
    const cond = c as {
      property?: string | { name?: string };
      operator?: string;
      value?: unknown;
    };
    const prop =
      typeof cond?.property === "string"
        ? cond.property
        : (cond?.property?.name ?? "unknown");
    const op = cond?.operator ?? "=";
    if (VALUELESS_OPERATORS.has(op)) {
      parts.push(`${prop}: ${op === "exists" ? "exists" : "does not exist"}`);
    } else {
      const val = String(cond?.value ?? "");
      parts.push(op === "=" ? `${prop}: ${val}` : `${prop}: ${op} ${val}`);
    }
  }

  const joiner =
    getGroupConditionType(conditions) === ConditionType.Or ? " OR " : " AND ";
  const suffix = parts.length > 3 ? "…" : "";
  return `${parts.slice(0, 3).join(joiner)}${suffix}`;
}

export function InlineFilterCell({
  modelKey,
  columnName,
  columns,
  isDisabled,
}: {
  modelKey: string;
  columnName: string;
  columns: FilterableColumn[];
  isDisabled: boolean;
}) {
  const { watch, setValue } = useFormContext<FeatureMatrixFormValues>();
  const columnConfig = watch(`relationships.${modelKey}.${columnName}`);
  const allModelColumns = watch(`relationships.${modelKey}`);
  const filterDialog = useDisclosure();
  const [editingFilter, setEditingFilter] = useState<InlineFilter | null>(null);

  const currentFilters: InlineFilter[] = columnConfig?.filters ?? [];
  const includeUnfiltered = columnConfig?.includeUnfiltered ?? false;

  const copyableFilters = (() => {
    const result: { filter: InlineFilter; sourceColumns: string[] }[] = [];

    for (const [name, config] of Object.entries(allModelColumns ?? {})) {
      if (name === columnName || !config.filters?.length) continue;
      for (const f of config.filters) {
        const key = JSON.stringify(f.conditions);
        const existing = result.find(
          (r) => JSON.stringify(r.filter.conditions) === key,
        );
        if (existing) {
          if (!existing.sourceColumns.includes(name)) {
            existing.sourceColumns.push(name);
          }
        } else {
          // Also skip filters already present on this column
          const alreadyOnColumn = currentFilters.some(
            (cf) => JSON.stringify(cf.conditions) === key,
          );
          if (!alreadyOnColumn) {
            result.push({ filter: f, sourceColumns: [name] });
          }
        }
      }
    }
    return result;
  })();

  const handleSaveFilter = (filter: InlineFilter) => {
    const existing = currentFilters.findIndex((f) => f.id === filter.id);
    const updated = [...currentFilters];
    if (existing >= 0) {
      updated[existing] = filter;
    } else {
      updated.push(filter);
    }
    setValue(`relationships.${modelKey}.${columnName}.filters`, updated, {
      shouldDirty: true,
    });
    filterDialog.onClose();
    setEditingFilter(null);
  };

  const setIncludeUnfiltered = (value: boolean) => {
    setValue(
      `relationships.${modelKey}.${columnName}.includeUnfiltered`,
      value,
      { shouldDirty: true },
    );
  };

  const handleDeleteFilter = (filterId: string) => {
    const updated = currentFilters.filter((f) => f.id !== filterId);
    setValue(`relationships.${modelKey}.${columnName}.filters`, updated, {
      shouldDirty: true,
    });
    // The flag is unreachable in the UI without a filter, so leaving it set
    // would silently resurrect the unfiltered variant on the next filter added.
    if (updated.length === 0) {
      setIncludeUnfiltered(false);
    }
  };

  const handleEditFilter = (filter: InlineFilter) => {
    setEditingFilter(filter);
    filterDialog.onOpen();
  };

  const handleCopyFilter = (filter: InlineFilter) => {
    const copied: InlineFilter = {
      id: crypto.randomUUID(),
      conditions: JSON.parse(JSON.stringify(filter.conditions)),
    };
    const updated = [...currentFilters, copied];
    setValue(`relationships.${modelKey}.${columnName}.filters`, updated, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <Row gap={1} align="center" minWidth={0} overflow="hidden">
        {currentFilters.length > 0 && (
          <Box as={BadgeGroup} minWidth={0} overflow="hidden">
            {currentFilters.map((f) => (
              <Box
                key={f.id}
                as={BadgeButton}
                rightActionProps={{
                  icon: CloseIcon,
                  "aria-label": "remove",
                  onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteFilter(f.id);
                  },
                }}
                onClick={() => handleEditFilter(f)}
                maxWidth="2xs"
              >
                <TextWithTooltip size="sm">
                  {inferFilterName(f.conditions)}
                </TextWithTooltip>
              </Box>
            ))}
            {includeUnfiltered && (
              <Badge
                rightActionProps={{
                  icon: CloseIcon,
                  "aria-label": "Stop computing unfiltered",
                  onClick: () => setIncludeUnfiltered(false),
                }}
              >
                Unfiltered
              </Badge>
            )}
          </Box>
        )}
        <Menu>
          <MenuIconButton
            icon={PlusIcon}
            aria-label="Add filter"
            size="sm"
            variant="secondary"
            isDisabled={isDisabled}
          />
          <MenuList>
            {copyableFilters.length > 0 && (
              <>
                {copyableFilters.map(({ filter }) => (
                  <MenuItem
                    key={filter.id}
                    onClick={() => handleCopyFilter(filter)}
                  >
                    <Box maxWidth="2xs">
                      <TextWithTooltip>
                        {inferFilterName(filter.conditions)}
                      </TextWithTooltip>
                    </Box>
                  </MenuItem>
                ))}
                <MenuDivider />
              </>
            )}
            <MenuItem
              onClick={() => {
                setEditingFilter(null);
                filterDialog.onOpen();
              }}
              icon={PlusIcon}
            >
              New filter
            </MenuItem>
            {currentFilters.length > 0 && !includeUnfiltered && (
              <>
                <MenuDivider />
                <MenuItem
                  icon={PlusIcon}
                  onClick={() => setIncludeUnfiltered(true)}
                >
                  Also compute unfiltered
                </MenuItem>
              </>
            )}
          </MenuList>
        </Menu>
      </Row>

      {filterDialog.isOpen && (
        <FilterDialog
          columns={columns}
          filter={editingFilter}
          onSave={handleSaveFilter}
          onClose={() => {
            filterDialog.onClose();
            setEditingFilter(null);
          }}
        />
      )}
    </>
  );
}

function FilterDialog({
  columns,
  filter,
  onSave,
  onClose,
}: {
  columns: FilterableColumn[];
  filter: InlineFilter | null;
  onSave: (filter: InlineFilter) => void;
  onClose: () => void;
}) {
  const [conditions, setConditions] = useState<
    Array<{ property: string; operator: string; value: string }>
  >(() => {
    const leaves = unwrapConditionLeaves(filter?.conditions ?? []) as Record<
      string,
      unknown
    >[];
    return leaves.length > 0
      ? leaves.map((c) => ({
          property:
            typeof c.property === "string"
              ? c.property
              : (((c.property as Record<string, unknown>)?.name as string) ??
                ""),
          operator: (c.operator as string) ?? "=",
          value: typeof c.value === "string" ? c.value : "",
        }))
      : [{ property: "", operator: "=", value: "" }];
  });

  const [conditionType, setConditionType] = useState<GroupConditionType>(() =>
    getGroupConditionType(filter?.conditions ?? []),
  );

  const addableConditionTypes: GroupConditionType[] =
    conditions.length > 1
      ? [conditionType]
      : [ConditionType.And, ConditionType.Or];

  const columnOptions = columns.map((c) => ({
    label: c.alias || c.name,
    value: c.name,
  }));

  const getColumnType = (propertyName: string): ColumnType => {
    const col = columns.find((c) => c.name === propertyName);
    return (col?.type as ColumnType) ?? ColumnType.String;
  };

  const getOperatorOptionsForColumn = (propertyName: string) => {
    const colType = getColumnType(propertyName);
    return (
      OperatorOptionsWithIsIn[colType] ??
      OperatorOptionsWithIsIn[ColumnType.String]
    ).filter(
      (op) =>
        (!OperatorsWithoutValue.includes(op.value) ||
          VALUELESS_OPERATORS.has(op.value)) &&
        !IntervalOperators.includes(op.value),
    );
  };

  const handleSave = () => {
    const leafConditions = conditions
      .filter(
        (c) => c.property && (c.value || VALUELESS_OPERATORS.has(c.operator)),
      )
      .map((c) => ({
        type: "property" as const,
        property: c.property,
        propertyType: getColumnType(c.property) as string,
        operator: c.operator,
        ...(VALUELESS_OPERATORS.has(c.operator) ? {} : { value: c.value }),
      }));

    const builtConditions: object[] =
      leafConditions.length > 1
        ? [{ type: conditionType, conditions: leafConditions }]
        : leafConditions;

    onSave({
      id: filter?.id ?? crypto.randomUUID(),
      conditions: builtConditions,
    });
  };

  const addCondition = () => {
    setConditions([...conditions, { property: "", operator: "=", value: "" }]);
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (
    index: number,
    field: "property" | "operator" | "value",
    value: string,
  ) => {
    const updated = [...conditions];
    const current = updated[index];
    if (!current) return;
    if (field === "property") {
      const colType = getColumnType(value);
      const validOps = getOperatorOptionsForColumn(value);
      const currentOp = current.operator;
      const isCurrentOpValid = validOps.some((op) => op.value === currentOp);
      updated[index] = {
        property: value,
        operator: isCurrentOpValid ? currentOp : DefaultOperators[colType],
        value: current.value,
      };
    } else if (field === "operator") {
      updated[index] = {
        property: current.property,
        operator: value,
        value: VALUELESS_OPERATORS.has(value) ? "" : current.value,
      };
    } else {
      updated[index] = {
        property: current.property,
        operator: current.operator,
        [field]: value,
      };
    }
    setConditions(updated);
  };

  return (
    <Dialog
      isOpen
      title={filter ? "Edit filter" : "Add filter"}
      variant="form"
      width="xl"
      actions={
        <Row gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            isDisabled={conditions.length === 0}
            onClick={handleSave}
          >
            Save
          </Button>
        </Row>
      }
      onClose={onClose}
    >
      <Column gap={4}>
        <Column gap={1}>
          <Text fontWeight="medium">Conditions</Text>
          <Column gap={2}>
            {conditions.map((condition, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: no reasonable id for conditions
              <Column key={index} gap={2}>
                {index > 0 && (
                  <AndOrToggleButton
                    conditionType={conditionType}
                    onClick={() =>
                      setConditionType(
                        conditionType === ConditionType.And
                          ? ConditionType.Or
                          : ConditionType.And,
                      )
                    }
                  />
                )}

                <Row>
                  <GroupIndicatorBar
                    conditionType={conditionType}
                    disableDrag
                  />
                  <Column
                    flex={1}
                    minWidth={0}
                    bg="white"
                    boxShadow="buttonShadowLight"
                    borderBottomWidth="hairline"
                    borderBottomStyle="solid"
                    borderRightWidth="hairline"
                    borderRightStyle="solid"
                    borderTopWidth="hairline"
                    borderTopStyle="solid"
                    borderColor="base.border"
                    borderBottomRightRadius="xl"
                    borderTopRightRadius="xl"
                  >
                    <Row gap={2} justify="space-between" p={4} align="center">
                      <Row gap={2} align="center" flex={1} minWidth={0}>
                        <Text
                          size="sm"
                          color="text.secondary"
                          whiteSpace="nowrap"
                        >
                          Where
                        </Text>
                        <Box flex={1} minWidth="120px">
                          <Combobox
                            width="100%"
                            options={columnOptions}
                            value={condition.property}
                            onChange={(val) =>
                              updateCondition(index, "property", val ?? "")
                            }
                            placeholder="Column"
                          />
                        </Box>
                        <Box minWidth="120px">
                          <Select
                            width="100%"
                            options={getOperatorOptionsForColumn(
                              condition.property,
                            )}
                            value={condition.operator}
                            onChange={(val) =>
                              updateCondition(index, "operator", val ?? "=")
                            }
                          />
                        </Box>
                        {!VALUELESS_OPERATORS.has(condition.operator) && (
                          <Box flex={1} minWidth="120px">
                            <TextInput
                              width="100%"
                              placeholder="Value"
                              value={condition.value}
                              onChange={(e) =>
                                updateCondition(index, "value", e.target.value)
                              }
                            />
                          </Box>
                        )}
                      </Row>
                      <DeleteButton onRemove={() => removeCondition(index)} />
                    </Row>
                  </Column>
                </Row>
              </Column>
            ))}
          </Column>
        </Column>

        <Row gap={2}>
          {addableConditionTypes.map((type) => (
            <AddConditionButton
              key={type}
              conditionType={type}
              onClick={() => {
                setConditionType(type);
                addCondition();
              }}
            />
          ))}
        </Row>
      </Column>
    </Dialog>
  );
}

function AddConditionButton({
  conditionType,
  onClick,
}: {
  conditionType: GroupConditionType;
  onClick: () => void;
}) {
  return (
    <Row
      as={Button}
      icon={FilterIcon}
      alignItems="center"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor={colors.hover[conditionType]}
      sx={{
        bg: colors.base[conditionType],
        color: colors.text[conditionType],
        _hover: {
          borderColor: colors.hover[conditionType],
          bg: colors.hover[conditionType],
          color: colors.textHover[conditionType],
          svg: { color: colors.textHover[conditionType] },
        },
        svg: {
          color: colors.text[conditionType],
        },
      }}
      onClick={onClick}
    >
      {conditionType === ConditionType.And
        ? "Add AND condition"
        : "Add OR condition"}
    </Row>
  );
}
